/* eslint-disable no-unused-vars */
import React from 'react';

import {
  GoogleAnalyticsConstants,
  GoogleAnalyticsDisplayedEvent,
  GoogleAnalyticsEventContextWrapper,
  useGoogleAnalyticsEventContext,
} from '@vvoyages/google-analytics';
import { Form, Formik } from 'formik';
import Image from 'next/image';

import * as Yup from 'yup';

import Button from '@/components/Button';
import ToolTip from '@/components/ToolTip';
import { WizardStep } from '@/components/Wizard';
import { appUiIds } from '@/constants/appUiIds';
import useAppUiData from '@/hooks/useAppUiData';

import styles from './ClaimBookingStep.module.scss';
import ClaimBookingDoBInput from './controls/ClaimBookingDoBInput';
import ClaimBookingFormInput from './controls/ClaimBookingFormInput';

interface ClaimBookingFormProps {
  tabId: string;
  onSubmit: Function;
  onCancel: () => void;
}

const DisplayingErrorMessagesSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  dob: Yup.string().required('Required'),
  bookingNumber: Yup.string()
    .required('Required')
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(6, 'Must be minimum 6 digits')
    .max(10, 'Must be maximum 10 digits'),
});

const ClaimBookingForm: React.FC<ClaimBookingFormProps> = ({ tabId, onSubmit, onCancel }) => {
  const googleAnalyticsEventContext = useGoogleAnalyticsEventContext();

  const appUiData = useAppUiData();
  const handleFormSubmit = async ({
    bookingNumber,
    name,
    dob,
  }: {
    bookingNumber: string;
    name: string;
    dob: string;
  }) => {
    const value = {
      bookingNumber,
      user: { lastName: name, birthday: dob },
      claimConfirmed: false,
      isManualEntry: true,
    };

    googleAnalyticsEventContext.event(GoogleAnalyticsConstants.EVENT_ACTION.FORM_COMPLETION, 'claim-a-booking', [
      'VV-account-sign-in|claim-a-booking',
    ]);

    onSubmit(value);
  };

  return (
    <WizardStep tabId={tabId}>
      <GoogleAnalyticsEventContextWrapper category="VV-account-sign-in|claim-a-booking">
        <GoogleAnalyticsDisplayedEvent eventAction={GoogleAnalyticsConstants.EVENT_ACTION.DISPLAYED} />
        <h2 className={styles.title}>{appUiData[appUiIds.claimBooking.title]}</h2>
        <p className={styles.subtitle}>{appUiData[appUiIds.claimBooking.enterDetailsManualEntry]}</p>
        <Formik
          initialValues={{
            name: '',
            bookingNumber: '',
            dob: '',
          }}
          validationSchema={DisplayingErrorMessagesSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleFormSubmit}
        >
          {({ values }) => {
            const isFormFilled = Object.values(values).every((field) => Boolean(field));
            return (
              <Form className={styles.manualForm}>
                <div className={styles.claimForm}>
                  <ClaimBookingFormInput name="name" label="Surname" required />
                  <ClaimBookingDoBInput name="dob" label="Date of Birth*" required />
                  <div className={styles.inputWithToolTip}>
                    <ClaimBookingFormInput name="bookingNumber" label="Booking Reference" required maxLength={10} />
                    <div className={styles.tooltipContainer}>
                      <ToolTip
                        classWrapper={styles.claimTooltip}
                        classTriangle={styles.tooltipTriangle}
                        widthInRem="14rem"
                        trackLabel="info|booking-reference"
                        parent={<Image src="/auth/v1/static/image/infoIcon.svg" width={16} height={16} alt="" />}
                      >
                        <p role="status">{appUiData[appUiIds.claimBooking.bookingReference]}</p>
                      </ToolTip>
                    </div>
                  </div>

                  <Button type="submit" variant="primary" disabled={!isFormFilled} className={styles.buttonClaimSubmit}>
                    {appUiData[appUiIds.commonLabels.search]}
                  </Button>
                  <Button onClick={onCancel} className={styles.cancelLink}>
                    {appUiData[appUiIds.commonLabels.cancel]}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </GoogleAnalyticsEventContextWrapper>
    </WizardStep>
  );
};

export default ClaimBookingForm;
